import { defineStore } from 'pinia'
import Cookies from 'js-cookie'
import { login, register, logout, getUserInfo } from '@/api/login-in-register'
import { clearDynamicRoutes, registerDynamicRoutes, registerNotLogRoutes } from '@/router/index'
import { buildMenuTree } from '@/utils/menuUtils'

// 用户状态管理
export const useUserStore = defineStore('user', {
  state: () => ({
    userInfo: null,
    isLoggedIn: false,
    token: Cookies.get('satoken') || null,
    // 用户基本信息字段
    userId: null,
    clientId: null,
    email: null,
    roles: [],
    permissions: [],
    menus: [],
    dynamicRoutes: [],
    isDeveloper: false
  }),

  getters: {
    getUserInfo: (state) => state.userInfo,
    getIsLoggedIn: (state) => state.isLoggedIn,
    getToken: (state) => state.token,
    getClientId: (state) => state.clientId,
    getEmail: (state) => state.email,
    getIsDeveloper: (state) => state.isDeveloper,
    getRoles: (state) => state.roles,
    getPermissions: (state) => state.permissions,
    getMenus: (state) => state.menus,
    getDynamicRoutes: (state) => state.dynamicRoutes,
    hasRole: (state) => (role) => state.roles.some(r => r.roleKey == role),
    hasPermission: (state) => (permission) => state.permissions.includes(permission),

  },

  actions: {
    // 设置用户信息
    setUserInfo(userInfo) {
      this.userInfo = userInfo
      // 提取并存储用户基本信息
      if (userInfo) {
        this.userId = userInfo.userId || null
        this.clientId = userInfo.clientId || null
        this.email = userInfo.email || null
      }
      if (userInfo?.roles) {
        this.roles = userInfo.roles
      }
      if (userInfo?.permissions) {
        this.permissions = userInfo.permissions
      }
      if (userInfo?.menus) {
        this.menus = buildMenuTree(userInfo.menus)
      }
    },

    // 设置登录状态
    setIsLoggedIn(status) {
      this.isLoggedIn = status
    },

    // 设置开发者状态
    setIsDeveloper(isDeveloper) {
      this.isDeveloper = isDeveloper
    },

    // 设置token（同时存储到cookie）
    setToken(token) {
      this.token = token
      if (token) {
        // 设置satoken cookie，7天过期，服务器端将从satoken字段获取token
        Cookies.set('satoken', token, { expires: 7, secure: false, sameSite: 'lax' })
        // 同时保留token字段用于前端逻辑判断
        // Cookies.set('token', token, { expires: 7, secure: false, sameSite: 'lax' })
      } else {
        Cookies.remove('satoken')
        // Cookies.remove('token')
      }
    },

    // 用户登录
    async login(credentials) {
      try {
        const response = await login(credentials)
        if (response.code === '200' || response.code === 200) {
          const { data } = response
          // 登录响应中data直接返回token字符串
          const token = data
          if (!token || typeof token !== 'string') {
            console.error('登录响应中未找到有效token:', data)
            return { success: false, message: '登录响应格式错误，未找到有效token' }
          }

          this.setToken(token)
          this.setIsLoggedIn(true)

          // 获取用户信息
          const userInfoResult = await this.getUserInfoFromServer()
          if (!userInfoResult.success) {
            // 获取用户信息失败，清除登录状态并返回失败
            this.clearUser()
            return { success: false, message: userInfoResult.message || '获取用户信息失败，请重新登录' }
          }

          return { success: true, data: response }
        } else {
          return { success: false, message: response.message || '登录失败' }
        }
      } catch (error) {
        console.error('登录失败:', error)
        return { success: false, message: error.message || '登录失败' }
      }
    },

    // 用户注册
    async register(userData) {
      try {
        const response = await register(userData)
        if (response.code === '200' || response.code === 200) {
          // 注册成功后自动登录
          const loginResult = await this.login({
            email: userData.email,
            password: userData.password
          })
          
          if (!loginResult.success) {
            // 自动登录失败，返回注册成功但需要手动登录的提示
            return { 
              success: true, 
              data: response, 
              needLogin: true,
              message: '注册成功，请手动登录' 
            }
          }
          
          return { success: true, data: response }
        } else {
          return { success: false, message: response.message || '注册失败' }
        }
      } catch (error) {
        console.error('注册失败:', error)
        return { success: false, message: error.message || '注册失败' }
      }
    },

    // 从服务器获取用户信息
    async getUserInfoFromServer() {
      try {
        // 在调用info接口前校验satoken是否存在
        const satoken = Cookies.get('satoken')
        if (!satoken) {
          console.log('satoken不存在，清除用户状态')
          this.clearUser()
          return { success: false, message: 'satoken不存在，需要重新登录' }
        }

        const response = await getUserInfo()
        if (response.code === '200' || response.code === 200) {
          const userInfo = response.data
          //   TODO 临时处理 应用管理
          if(userInfo.roles[0].id == 1 && !userInfo.approved) {
            userInfo.menus = userInfo.menus.filter(item => item.path != '/personal-center/app-manage')
          }

          this.setUserInfo(userInfo)
          this.setIsLoggedIn(true)

          // 通过info接口返回的applyed字段判断开发者状态
          this.setIsDeveloper(userInfo.applyed || false)
          console.log('response', response)
          return { success: true, data: userInfo }
        } else {
          console.error('获取用户信息失败:', response.message)
          // 如果获取用户信息失败，可能token已过期，清除登录状态
          this.clearUser()
          return { success: false, message: response.message || '获取用户信息失败' }
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.clearUser()
        return { success: false, message: error.message || '获取用户信息失败' }
      }
    },

    // 用户登出
    async logout() {
      try {
        await logout()
      } catch (error) {
        console.error('登出请求失败:', error)
      } finally {
        // 无论登出请求是否成功，都清除本地状态和动态路由
        this.clearUser()
        clearDynamicRoutes()
        // 重新注册未登录路由，确保退出登录后能正常访问公开页面
        registerNotLogRoutes()
      }
    },

    // 设置菜单数据
    setMenus(menus) {
      this.menus = buildMenuTree(menus)
    },

    // 清除用户状态
    clearUser() {
      this.userInfo = null
      this.isLoggedIn = false
      this.setToken(null)
      // 清除用户基本信息
      this.userId = null
      this.clientId = null
      this.email = null
      this.roles = []
      this.permissions = []
      this.menus = []
      this.dynamicRoutes = []
      this.isDeveloper = false
    },

    // 初始化用户状态（应用启动时调用）
    async initUserState() {
      const token = Cookies.get('satoken')
      if (token) {
        this.token = token
        try {
          // 尝试获取用户信息验证token有效性
          const result = await this.getUserInfoFromServer()
          if (!result.success) {
            // token无效，清除状态
            this.clearUser()
          }
        } catch (error) {
          console.error('初始化用户状态失败:', error)
          this.clearUser()
        }
      } else {
        this.clearUser()
      }
    }
  },

  persist: {
    key: 'user-store',
    storage: localStorage,
    paths: ['userInfo', 'isLoggedIn', 'userId', 'clientId', 'email', 'roles', 'permissions', 'menus', 'dynamicRoutes', 'isDeveloper']
  }
})